// src/hooks/integrations/facebook/types.ts

import { z } from 'zod';
import type { WhatsAppIntegration } from 'src/api/generated';

export const COEXISTENCE_PHONE_NUMBER_ID_PLACEHOLDER = 'COEXISTENCE_FLOW';

export interface FacebookWindow {
  FB: {
    init: (params: FBInitParams) => void;
    login: (
      callback: (response: FacebookLoginResponse) => void,
      options: FBLoginOptions
    ) => void;
    api: (path: string, callback: (response: unknown) => void) => void;
  };
  fbAsyncInit: () => void;
}

export interface FacebookAuthResponse {
  code: string;
  expiresIn: number | null;
  userID: string | null;
}

export interface FacebookLoginResponse {
  authResponse: FacebookAuthResponse | null;
  status: string;
}

export interface WhatsAppSessionInfo {
  phone_number_id: string;
  waba_id: string;
}

/** Billing context persisted by the backend before opening Embedded Signup. */
export interface WhatsAppOnboardingConfiguration {
  /** Durable server-side attempt identifier bound to integration creation. */
  attemptId: string;
  /** Product-level billing ownership captured for this attempt. */
  billingMode: 'customer_managed' | 'comos_managed';
  /** Meta Multi-Partner Solution identifier for Comos-managed billing. */
  solutionId?: string;
}

/** Correlated data produced by one completed Embedded Signup attempt. */
export interface WhatsAppSignupResult {
  /** Monotonic browser-local identifier for the completed attempt. */
  attemptId: number;
  /** Durable backend attempt identifier used to finalize this exact signup. */
  onboardingAttemptId: string;
  /** Billing mode captured before the Meta popup opened. */
  billingMode: 'customer_managed' | 'comos_managed';
  /** Optional WhatsApp asset hints returned by the Meta dialog. */
  sessionInfo: WhatsAppSessionInfo | null;
  /** Authorization-code response returned by Facebook Login. */
  loginResponse: FacebookLoginResponse;
}

export interface UseWhatsAppSignupParams {
  /** Receives a safe reason when the active Meta attempt cannot complete. */
  onSignupFailure?: (reason: WhatsAppSignupFailureReason) => void;
}

export interface ActiveWhatsAppSignupAttempt {
  /** Monotonic identifier captured by every callback for this attempt. */
  attemptId: number;
  /** Durable billing configuration captured before this popup opened. */
  onboardingConfiguration: WhatsAppOnboardingConfiguration;
  /** Facebook Login response for this attempt, when received. */
  loginResponse: FacebookLoginResponse | null;
  /** Meta completion identifiers for this attempt, when received. */
  sessionInfo: WhatsAppSessionInfo | null;
  /** Popup source captured at launch or bound from this attempt's messages. */
  messageSource: object | null;
  /** Browser popup captured at launch, separate from untrusted message sources. */
  popup: Window | null;
}

export interface WhatsAppSignupMessageResolution {
  /** Lifecycle action the active signup attempt must take. */
  action: 'cancel' | 'failure' | 'ignore' | 'session';
  /** Safe failure class when the provider event cannot be completed. */
  failureReason?: WhatsAppSignupFailureReason;
  /** Normalized WhatsApp identifiers for a supported completion. */
  sessionInfo?: WhatsAppSessionInfo;
}

export interface UseStoreWhatsAppIntegrationProps {
  /** Correlated one-shot result from a completed Embedded Signup attempt. */
  signupResult: WhatsAppSignupResult | null;
  /** Called after the backend has stored and classified the integration. */
  onSuccess?: (integration: WhatsAppIntegration) => void;
  /** Called when finalization fails before a recoverable result is returned. */
  onError?: () => void;
}

export interface UseStoreWhatsAppIntegrationReturn {
  loading: boolean;
  error: Error | null;
  integration: WhatsAppIntegration | null;
}

/** User-facing failure classes emitted by the Embedded Signup boundary. */
export const WHATSAPP_SIGNUP_FAILURE_REASONS = {
  CANCELLED: 'cancelled',
  CONFIGURATION_ERROR: 'configuration-error',
  INCOMPLETE_SESSION: 'incomplete-session',
  META_ERROR: 'meta-error',
  TIMEOUT: 'timeout',
  UNSUPPORTED_COMPLETION: 'unsupported-completion',
} as const;

/** A normalized failure that the integration card can present safely. */
export type WhatsAppSignupFailureReason =
  (typeof WHATSAPP_SIGNUP_FAILURE_REASONS)[keyof typeof WHATSAPP_SIGNUP_FAILURE_REASONS];

/** Validated message emitted by Meta's WhatsApp Embedded Signup dialog. */
export interface PostMessageEvent {
  /** Identifies the message as a WhatsApp Embedded Signup event. */
  type: 'WA_EMBEDDED_SIGNUP';
  /** Completion, cancellation, or error state reported by Meta. */
  event:
    | 'FINISH'
    | 'FINISH_ONLY_WABA'
    | 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING'
    | 'FINISH_OBO_MIGRATION'
    | 'FINISH_GRANT_ONLY_API_ACCESS'
    | 'CANCEL'
    | 'ERROR';
  /** Version-dependent identifiers and diagnostic metadata. */
  data: {
    /** WhatsApp phone-number ID selected during signup, when present. */
    phone_number_id?: string;
    /** WhatsApp Business Account ID selected during signup, when present. */
    waba_id?: string;
    /** WABA IDs returned by Meta flows that support multiple accounts. */
    waba_ids?: string[];
    /** Signup step at which a cancellation occurred. */
    current_step?: string;
    /** Meta-provided signup error message. */
    error_message?: string;
    /** Meta-provided signup error code. */
    error_code?: string | number;
    /** Meta signup session identifier. */
    session_id?: string;
    /** Meta-provided event timestamp. */
    timestamp?: string | number;
  };
}

/** Runtime validator for messages received from the Facebook dialog. */
export const PostMessageEventSchema: z.ZodType<PostMessageEvent> = z.object({
  type: z.literal('WA_EMBEDDED_SIGNUP'),
  event: z.enum([
    'FINISH',
    'FINISH_ONLY_WABA',
    'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING',
    'FINISH_OBO_MIGRATION',
    'FINISH_GRANT_ONLY_API_ACCESS',
    'CANCEL',
    'ERROR',
  ]),
  data: z
    .object({
      phone_number_id: z.string().optional(),
      waba_id: z.string().optional(),
      waba_ids: z.array(z.string()).optional(),
      current_step: z.string().optional(),
      error_message: z.string().optional(),
      error_code: z.union([z.string(), z.number()]).optional(),
      session_id: z.string().optional(),
      timestamp: z.union([z.string(), z.number()]).optional(),
    })
    .passthrough(),
});

export interface FBInitParams {
  appId: string;
  autoLogAppEvents: boolean;
  xfbml: boolean;
  version: string;
}

export interface FBLoginOptions {
  /** Facebook Login for Business configuration ID. */
  config_id: string;
  /** Requests the short-lived authorization code response. */
  response_type: string;
  /** Forces code response semantics for Embedded Signup. */
  override_default_response_type: boolean;
  /** WhatsApp Embedded Signup launch options. */
  extras: {
    /** Embedded Signup setup object expected by the active Meta configuration. */
    setup: {
      /** Case-sensitive Meta Multi-Partner Solution field. */
      solutionID?: string;
    };
    [key: string]: unknown;
  };
}
